import React from 'react';
import { Button } from '@/components/ui/button';
import { Briefcase, ArrowUpRight, FileText, Calculator, Building2, ShieldCheck } from 'lucide-react';

/**
 * Cross-promotion block linking to the parent firm, TRJ Services.
 *
 * Uses the same navy / gold / white palette and typography as the rest of
 * the academy site. Lazy by default (compact light variant) — pass
 * `variant="dark"` for the homepage hero band.
 */
export default function TRJServicesCTA({ variant = 'light' }) {
  const isDark = variant === 'dark';
  return (
    <section
      data-testid="trj-services-cta"
      className={isDark ? 'py-20 bg-navy text-white relative overflow-hidden' : 'py-16 bg-trjlight'}
    >
      {isDark && <div className="absolute inset-0 hero-grain opacity-40" />}
      <div className={`relative max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center ${isDark ? '' : ''}`}>
        <div>
          <span className={`trj-divider ${isDark ? 'text-gold' : ''}`}>Our parent organization</span>
          <h2 className={`font-display font-semibold text-3xl lg:text-4xl mt-5 leading-tight ${isDark ? 'text-white' : 'text-navy'}`}>
            Need Professional <span className="text-gold font-serif italic">Tax, Accounting,</span> or Business Services?
          </h2>
          <p className={`mt-4 max-w-2xl text-base leading-relaxed ${isDark ? 'text-white/75' : 'text-slate-600'}`}>
            Looking for professional tax preparation, bookkeeping, accounting, nonprofit support,
            tax resolution, or business advisory services? Our parent organization,{' '}
            <span className={isDark ? 'text-gold font-semibold' : 'text-navy font-semibold'}>TRJ Services</span>, provides comprehensive financial and business solutions for
            individuals, businesses, and nonprofit organizations.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="https://trjsvc.com"
              target="_self"
              rel="noopener"
              data-testid="visit-trj-services-btn"
            >
              <Button className="btn-gold h-12 px-7 text-base">
                <Briefcase className="h-4 w-4 mr-2" />
                Visit TRJ Services
                <ArrowUpRight className="h-4 w-4 ml-1.5" />
              </Button>
            </a>
            <a
              href="https://trjsvc.com"
              target="_self"
              rel="noopener"
              className={`inline-flex items-center text-sm font-semibold underline-offset-4 hover:underline ${isDark ? 'text-white/80 hover:text-gold' : 'text-navy hover:text-gold'} px-2 h-12`}
            >
              trjsvc.com →
            </a>
          </div>
        </div>

        {/* Right panel — service highlights */}
        <div className={`grid grid-cols-2 gap-3 ${isDark ? '' : ''}`}>
          {[
            { icon: FileText,   label: 'Tax Preparation' },
            { icon: Calculator, label: 'Bookkeeping & Accounting' },
            { icon: Building2,  label: 'Nonprofit Support' },
            { icon: ShieldCheck,label: 'Tax Resolution' },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className={`rounded-xl border ${isDark ? 'border-white/15 bg-white/5 backdrop-blur-sm' : 'border-slate-200 bg-white'} p-4 flex items-start gap-3`}
            >
              <span className={`shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md ${isDark ? 'bg-gold/15 text-gold' : 'bg-gold/15 text-navy'}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className={`text-sm font-semibold leading-snug ${isDark ? 'text-white' : 'text-navy'}`}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
